import { useEffect, useState } from "react";
import { api, formatBRL, formatDateBR } from "../../lib/api";
import { FileText, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function ClientContratos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [signing, setSigning] = useState(null);
  const [signature, setSignature] = useState({ accepted: true, full_name: "", cpf_cnpj: "" });

  const load = () => api.get("/contracts").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openSignature = (contract) => {
    setSigning(contract);
    setSignature({ accepted: true, full_name: user?.name || "", cpf_cnpj: user?.document || "" });
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/contracts/${signing.id}/sign`, signature);
      toast.success("Contrato assinado com sucesso!");
      setSigning(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao assinar");
    }
  };

  return (
    <div data-testid="client-contratos">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Contratos</div>
        <h1 className="font-display text-3xl text-[#14213D] font-light">Meus Contratos</h1>
      </div>

      <div className="space-y-4">
        {items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
            <FileText size={32} className="mx-auto mb-3 text-gray-300" />
            Você ainda não tem contratos
          </div>
        ) : items.map((c) => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`contract-${c.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-[#14213D] text-[#D4AF37] flex items-center justify-center flex-shrink-0"><FileText size={20} /></div>
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-display text-lg text-[#14213D]">{c.title}</div>
                  <div className="flex items-center gap-2">
                    {c.signature?.signed ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 flex items-center gap-1">
                        <ShieldCheck size={11} /> Assinado
                      </span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold bg-amber-50 text-amber-700">
                        Aguardando assinatura
                      </span>
                    )}
                    <span className="text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold bg-blue-50 text-blue-700">{c.status}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Vigência: {formatDateBR(c.start_date)} → {c.end_date ? formatDateBR(c.end_date) : "indeterminado"}
                </div>
                <div className="font-semibold text-[#14213D] mt-2">{formatBRL(c.monthly_value)}<span className="text-xs text-gray-500 font-normal">/mês</span></div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-700 whitespace-pre-line">{c.body}</div>

                {c.signature?.signed && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900" data-testid={`sig-info-${c.id}`}>
                    <div className="font-semibold flex items-center gap-1"><ShieldCheck size={12} /> Documento assinado eletronicamente</div>
                    <div className="mt-1 grid grid-cols-2 gap-1">
                      <div>Assinante: {c.signature.full_name}</div>
                      <div>Data: {new Date(c.signature.signed_at).toLocaleString("pt-BR")}</div>
                      <div>IP: {c.signature.ip || "—"}</div>
                      <div className="truncate" title={c.signature.document_hash}>Hash: {c.signature.document_hash?.slice(0, 16)}…</div>
                    </div>
                  </div>
                )}

                {!c.signature?.signed && (
                  <button
                    onClick={() => openSignature(c)}
                    className="mt-4 bg-[#14213D] hover:bg-[#1F335E] text-white font-semibold px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"
                    data-testid={`sign-${c.id}`}
                  >
                    <ShieldCheck size={14} /> Assinar contrato
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Signature Modal */}
      {signing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="signature-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSigning(null)} />
          <div className="relative bg-white rounded-lg w-full max-w-md p-6 shadow-2xl">
            <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Assinatura eletrônica</div>
            <h3 className="font-display text-xl text-[#14213D] mb-4">Confirmar assinatura</h3>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4 text-xs text-gray-700">
              Ao assinar, registramos seu nome, IP, data/hora, navegador e um hash SHA-256 imutável do contrato. Este registro tem validade jurídica conforme a MP 2.200-2/2001.
            </div>
            <form onSubmit={submitSignature} className="space-y-3" data-testid="signature-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Nome completo *</label>
                <input
                  required value={signature.full_name}
                  onChange={(e) => setSignature({ ...signature, full_name: e.target.value })}
                  className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm"
                  data-testid="sig-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">CPF/CNPJ</label>
                <input
                  value={signature.cpf_cnpj}
                  onChange={(e) => setSignature({ ...signature, cpf_cnpj: e.target.value })}
                  className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm"
                  data-testid="sig-cpf"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-700 pt-2">
                <input
                  type="checkbox" required checked={signature.accepted}
                  onChange={(e) => setSignature({ ...signature, accepted: e.target.checked })}
                  data-testid="sig-accept"
                  className="mt-0.5"
                />
                Li e aceito integralmente os termos deste contrato.
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setSigning(null)} className="flex-1 border border-gray-300 text-gray-700 rounded py-2.5 text-sm font-semibold">Cancelar</button>
                <button type="submit" className="flex-1 bg-[#D4AF37] text-[#14213D] rounded py-2.5 text-sm font-bold flex items-center justify-center gap-1" data-testid="sig-submit">
                  <CheckCircle2 size={14} /> Confirmar assinatura
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
